import { Fragment, useState, useEffect } from 'react';
import { AlertCircle, FileText, FileDown, ChevronDown, Headphones, Loader2 } from "lucide-react";
import { format, parseISO } from 'date-fns';
import {
  Alert,
  AlertDescription,
  Text,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DataPagination,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  DataTableHeader,
  DataTableBody,
  DataTableRow,
  DataTableHead,
  DataTableCell,
  Skeleton,
  Checkbox,
  Label,
  Switch,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  useToast,
} from "@musa.tech/design-system";
import { useInvoiceCollectsCSVExport } from "@/hooks/useInvoiceCollectsCSVExport";
import { useInvoiceDetails } from "@/hooks/useInvoiceDetails";
import { useSetDetailBreadcrumbLabel } from "@/contexts/DetailBreadcrumbContext";
import { useApproveMeasurement } from "@/hooks/useApproveMeasurement";
import { useAnalytics } from "@/hooks/useAnalytics";
import { openCloudHumansChat } from "@/utils/cloudHumans";
import { fetchInvoiceBankSlipUrl, type CrParcela, type NfItem } from "@/integrations/faturamento/client";
import { InvoiceDocumentModal } from "./InvoiceDocumentModal";
import { ApproveMeasurementPoDialog } from "./ApproveMeasurementPoDialog";
import { DetailField } from "./DetailField";
import { InvoiceStatusBadge } from "./InvoiceStatusBadge";
import { TableSkeleton } from "./FinanceSkeletons";
import { CollectCalculationDetails } from "./CollectCalculationDetails";
import { MtrCell } from "./MtrCell";
import { TaxChangeNoticeAccordion } from "./TaxChangeNoticeAccordion";
import { useDemoMode } from "@/contexts/DemoModeContext";
import { maskGeneratorName, maskCNPJ, maskEstablishmentName, maskNumericValue } from "@/utils/demoMode";
import { useGeneratorContestations } from "@/hooks/useContestations";
import type { Contestation } from "@/integrations/contestacao/client";
import { MeasurementStatusBadge } from "./contestacao/MeasurementStatusBadge";
import { ContestationHistory } from "./contestacao/ContestationHistory";
import { MeasurementHistory } from "./contestacao/MeasurementHistory";
import { useMeasurementApproval } from "@/hooks/useMeasurementApproval";
import { usePermissions } from "@/hooks/useUnifiedAuth";
import { PERMISSIONS } from "@/constants/permissions";
import { ContestationForm } from "./contestacao/ContestationForm";
import { CollectStatusBadge } from "./contestacao/CollectStatusBadge";
import { COLLECT_STATUS_CONTESTED } from "./contestacao/collectStatus";
import { MEASUREMENT_MOTIVE_GROUPS, collectMotiveGroups } from "./contestacao/contestationOptions";
import { canContest, formatDeadline, retentionWindowDeadline } from "./contestacao/contestationWindow";

/**
 * Contestação com desfecho — decidida pela Musa, aceita ou rejeitada. `CORRECTION_PENDING` fica
 * fora: a decisão saiu, mas a correção ainda está sendo aplicada nos outros sistemas, e o valor
 * da medição pode mudar até o fim disso.
 */
const RESOLVED_STATUSES = new Set(['ACCEPTED', 'PARTIALLY_ACCEPTED', 'REJECTED', 'CORRECTION_APPLIED']);


const MONTHS_PT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function formatCompetencia(billingPeriod: string): string {
  const [year, month] = billingPeriod.split('-');
  const idx = parseInt(month, 10) - 1;
  if (!year || isNaN(idx) || idx < 0 || idx > 11) return billingPeriod;
  return `${MONTHS_PT[idx]}/${year}`;
}

function formatDueDate(dueDate: string | null): string {
  if (!dueDate) return '—';
  try {
    return format(parseISO(dueDate), 'dd/MM/yyyy');
  } catch {
    return dueDate;
  }
}

function formatCnpj(doc: string): string {
  const d = doc.replace(/\D/g, '');
  if (d.length !== 14) return doc;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatDate(date: string | null | undefined): string {
  if (!date) return '';
  try {
    return format(parseISO(date), 'dd/MM/yyyy');
  } catch {
    return date;
  }
}

type ModalState = {
  open: boolean;
  tipo: 'boleto' | 'nf';
  url: string | null;
  isLoading: boolean;
  fatura: { title: string; subtitle: string } | null;
};

const MODAL_CLOSED: ModalState = { open: false, tipo: 'boleto', url: null, isLoading: false, fatura: null };

interface InvoiceDetailContentProps {
  id: string;
}

export function InvoiceDetailContent({ id }: InvoiceDetailContentProps) {
  const { isDemoMode } = useDemoMode();
  const { trackButtonClick, trackFinancialRuleView } = useAnalytics();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<10 | 25 | 50>(10);
  const [modal, setModal] = useState<ModalState>(MODAL_CLOSED);
  /** O card de escolha está aberto — só some quando o gerador desiste de contestar. */
  const [choosingContestation, setChoosingContestation] = useState(false);
  /**
   * O que está sendo contestado. Um valor só, e não dois booleanos: as duas opções são
   * mutuamente exclusivas, e representá-las como estados independentes deixava representável um
   * "contestando os dois ao mesmo tempo" que o fluxo não suporta.
   */
  const [contestationMode, setContestationMode] = useState<'measurement' | 'collects' | null>(null);
  /** Coletas marcadas para contestar — cada uma abre o próprio formulário. */
  const [selectedCollects, setSelectedCollects] = useState<Set<string>>(new Set());

  const contestingMeasurement = contestationMode === 'measurement';
  /** Modo de seleção da tabela: a coluna de checkbox só existe quando ligado. */
  const selectingCollects = contestationMode === 'collects';
  /** Coletas com a linha aninhada de "Cálculo aplicado" aberta. */
  const [expandedCalc, setExpandedCalc] = useState<Set<string>>(new Set());
  const [confirmingApproval, setConfirmingApproval] = useState(false);
  const { toast } = useToast();
  const approve = useApproveMeasurement();
  const { hasPermission } = usePermissions();

  /**
   * Contestar e aprovar são a mesma permissão (GR-637) — o catálogo define uma
   * role para as duas: `dispute-measurement`, do perfil `measurement-approval`.
   *
   * `hasPermission` e não `hasBaselinePermission`: acesso aditivo, sem a
   * escapatória do não migrado. Remove a ação de quem não tem o perfil, e a
   * migração não o atribui — decisão consciente.
   */
  const canDecideMeasurement = hasPermission(PERMISSIONS.DISPUTE_MEASUREMENT);

  const toggleCollectSelection = (collectId: string) => {
    setSelectedCollects(current => {
      const next = new Set(current);
      if (next.has(collectId)) {
        next.delete(collectId);
      } else {
        next.add(collectId);
      }
      return next;
    });
  };

  /** Desliga o modo escolhido, mas mantém o card aberto — dá para trocar de opção sem recomeçar. */
  const exitSelection = () => {
    setContestationMode(null);
    setSelectedCollects(new Set());
  };

  /** Desiste de contestar: fecha o card e limpa o que estava marcado. */
  const cancelContestation = () => {
    setChoosingContestation(false);
    exitSelection();
  };

  /** Os dois interruptores são excludentes: ligar um desliga o outro por construção. */
  const pickContestationMode = (mode: 'measurement' | 'collects', on: boolean) => {
    setSelectedCollects(new Set());
    setContestationMode(on ? mode : null);
  };

  const toggleCalc = (collectId: string) => {
    setExpandedCalc(current => {
      const next = new Set(current);
      if (next.has(collectId)) {
        next.delete(collectId);
      } else {
        next.add(collectId);
        // Mesmo evento que o popover do GR-497 disparava ao abrir.
        trackFinancialRuleView({ collectionId: collectId });
      }
      return next;
    });
  };

  const { data, isLoading, isFetching, error } = useInvoiceDetails({ id, page, page_size: pageSize });
  // A fatura existe assim que há OS na Omie — mesmo critério usado no extrato (OrdersTable).
  useSetDetailBreadcrumbLabel(data ? (data.invoice.os_code ? 'Fatura' : 'Medição') : undefined);
  // Consulta própria, e o `error` dela é ignorado de propósito: a tela não depende da aprovação.
  // Ver `useMeasurementApproval`.
  const { data: approval } = useMeasurementApproval(id);
  const {
    index: contestationIndex,
    isLoading: isLoadingContestations,
    isError: contestationsUnavailable,
  } = useGeneratorContestations();

  const isFixedValue = data?.invoice?.is_fixed_value ?? false;

  /**
   * Contestação ativa que marca a linha da coleta: a da própria coleta ou a do lançamento dela.
   * A da medição inteira não entra — quem passa a CONTESTADO é o ciclo (RN-16), não a coleta, e a
   * RFC permite contestar uma coleta cuja fatura já está contestada (RN-40/S19).
   *
   * Definida aqui, **antes dos retornos de loading/erro**, porque o export em CSV (MC-292) também
   * a consome: a coluna "Status coleta" do arquivo tem de sair com a mesma regra da tabela, e o
   * hook de export é chamado neste ponto do componente.
   */
  const activeForCollect = (collect: { id: string; transaction_id?: string | null }) =>
    contestationIndex.active('COLLECT', collect.id) ??
    (collect.transaction_id ? contestationIndex.active('TRANSACTION', collect.transaction_id) : undefined);

  const { exportCSV, isExporting } = useInvoiceCollectsCSVExport({
    invoiceId: id,
    numeroPedido: data?.invoice?.numero_pedido ?? null,
    osCode: data?.invoice?.os_code ?? null,
    sfmId: data?.invoice?.sfm_id ?? id,
    billingPeriod: data?.invoice?.billing_period ?? '',
    isFixedValue,
    hasActiveContestation: collect => !!activeForCollect(collect),
    contestationsUnavailable,
  });

  useEffect(() => {
    setPage(1);
  }, [id]);

  if (isLoading) {
    return (
      <div className="px-6 py-4 space-y-4 min-h-[400px]">
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-12 px-6">
        <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
        <Text>Não foi possível carregar os detalhes da fatura.</Text>
      </div>
    );
  }

  const { invoice, collects, breakdown } = data;
  // Em análise interna não existe nem número de pedido nem OS na Omie — resta o id do ciclo.
  const numeroFatura = invoice.numero_pedido ?? invoice.os_code ?? invoice.sfm_id;
  /** A medição já virou fatura: tem OS na Omie, logo tem número de pedido e cobrança. */
  const isInvoiced = !!invoice.os_code;
  /**
   * Suporte aparece em CONTESTED e também em READY_TO_INVOICE (liberada, ainda sem OS): nessa
   * fase o gerador não tem o botão de contestar — é o intervalo da RN em `contestationWindow` —,
   * e ficaria sem nenhum canal para tirar dúvida sobre o ciclo até a fatura sair.
   */
  const showsSupportContact =
    invoice.release_status === 'CONTESTED' || invoice.release_status === 'READY_TO_INVOICE';

  /**
   * Cliente que exige PO (EDD-582): o botão abre o modal com o campo da PO em vez da confirmação
   * simples. O modal cuida do aviso de PO repetida; o que sobra aqui é o sucesso.
   */
  const requiresPo = !!invoice.requires_po;

  const handleApproveWithPo = async (po: { po_number: string; confirm_duplicate: boolean }) => {
    trackButtonClick({ buttonLabel: 'financeiro_aprovar_medicao_com_po' });
    await approve.mutateAsync({ id: invoice.sfm_id, po });
    setConfirmingApproval(false);
    toast({
      title: 'Medição aprovada',
      description: 'A PO foi registrada e a medição segue para faturamento.',
      variant: 'success',
    });
  };

  const handleApprove = async () => {
    trackButtonClick({ buttonLabel: 'financeiro_aprovar_medicao' });
    try {
      await approve.mutateAsync({ id: invoice.sfm_id });
      setConfirmingApproval(false);
      toast({
        title: 'Medição aprovada',
        description: 'A medição segue para faturamento.',
        variant: 'success',
      });
    } catch (error) {
      setConfirmingApproval(false);
      toast({
        title: 'Não foi possível aprovar a medição',
        description: error instanceof Error ? error.message : 'Tente novamente.',
        variant: 'error',
      });
    }
  };

  /**
   * O `collect_count` vai no evento de propósito (MC-292): o volume de coletas por ciclo não está
   * documentado em lugar nenhum, e é ele que decide se o download paginado no cliente continua
   * sendo o desenho certo. Medir pelo uso real custa este parâmetro.
   */
  const handleExportCSV = () => {
    trackButtonClick({
      buttonLabel: 'financeiro_exportar_csv_coletas',
      fatura_id: id,
      collect_count: collects.pagination.count,
    });
    exportCSV();
  };

  const deadline = retentionWindowDeadline({
    releaseStatus: invoice.release_status,
    releaseAt: invoice.release_at,
    isInvoiced,
  });

  /**
   * Contestação ativa da medição inteira. Serve às pré-condições de abertura (RN-03 no botão,
   * RN-04 nos motivos da linha) — **não** ao estado exibido: quem transiciona para CONTESTADO é o
   * ciclo, via `billing_cycle.hold` (RN-16/RN-23), e o portal apenas reflete o `release_status`.
   */
  const measurementContestation = contestationIndex.active('BILLING_CYCLE', invoice.sfm_id);

  /**
   * Contestações desta medição, das duas fontes — e é a união que importa.
   *
   * Cruzar pelos objetos da tela perdia a contestação cujo objeto saiu da tela: aceitar "coleta
   * não realizada" invalida as transações da coleta, ela desaparece da listagem da medição, e com
   * ela desaparecia o histórico da própria contestação que a impediu. Para o gerador, o pedido e a
   * resposta simplesmente sumiam.
   *
   * `affectingCycle` cobre esse caso pelo vínculo gravado na abertura — que inclui o ciclo já
   * faturado, não só os bloqueáveis. O cruzamento por objeto fica como rede para contestação
   * aberta antes da coluna existir, que tem a lista vazia.
   */
  const cycleContestations = Array.from(
    new Map(
      [
        ...contestationIndex.affectingCycle(invoice.sfm_id),
        ...contestationIndex.all('BILLING_CYCLE', invoice.sfm_id),
        ...collects.data.flatMap(c => [
          ...contestationIndex.all('COLLECT', c.id),
          ...(c.transaction_id ? contestationIndex.all('TRANSACTION', c.transaction_id) : []),
        ]),
      ].map(c => [c.id, c]),
    ).values(),
  ).sort((a, b) => b.created_at.localeCompare(a.created_at));

  /**
   * Alguma contestação desta medição já teve desfecho. Fecha a fase pré-faturamento em
   * `canContest`: o rito terminou, e reabrir só faz sentido quando a fatura existir.
   */
  const hasResolvedContestation = cycleContestations.some(c => RESOLVED_STATUSES.has(c.status));

  /**
   * Sem a listagem de contestações não há como saber o que já está contestado, e a RN-03 recusaria
   * a abertura com `409`. Oferecer o botão nesse estado leva o gerador a preencher o formulário para
   * receber um erro no envio — por isso a ação fica indisponível enquanto a consulta não volta.
   */
  // Aqui e não em cada botão: `isContestable` é a raiz dos dois caminhos, o da
  // medição inteira e o por coleta, que só é alcançável a partir dele.
  const isContestable =
    canDecideMeasurement &&
    canContest({
      releaseStatus: invoice.release_status,
      publishedAt: invoice.published_at,
      releaseAt: invoice.release_at,
      isInvoiced,
      hasResolvedContestation,
    }) &&
    !contestationsUnavailable;

  /**
   * Aprovar libera o ciclo para o faturamento antes do fim da janela — só cabe em
   * AWAITING_GENERATOR, e **só enquanto nada nesta medição tiver sido contestado**.
   *
   * O `release_status` sozinho não basta. Ele volta a AWAITING_GENERATOR quando a última
   * contestação é resolvida (`billing_cycle.unhold`, RN-BE-07), então o botão reaparecia depois
   * da resolução — e aprovar ali significaria o gerador dizendo "concordo com a medição" sobre
   * uma medição que ele mesmo disputou e que já seguiu o rito da contestação. Durante a
   * contestação o estado é CONTESTED e o botão já saía, mas por um caminho que depende do evento
   * de hold ter chegado: enquanto ele não chega, o ciclo continua AWAITING_GENERATOR.
   *
   * `cycleContestations` é o histórico completo — qualquer status, e nos três object_type da
   * medição. Uma contestação de coleta também conta: aprovar mandaria ao Omie uma fatura que
   * contém objeto disputado.
   *
   * Consulta indisponível ou ainda carregando esconde o botão, pelo mesmo motivo que esconde o
   * de contestar: sem a listagem não há como saber se já houve contestação, e o erro cai para o
   * lado seguro.
   */
  const isApprovable =
    canDecideMeasurement &&
    invoice.release_status === 'AWAITING_GENERATOR' &&
    cycleContestations.length === 0 &&
    !contestationsUnavailable &&
    !isLoadingContestations;

  const collectLabel = (collectId: string): string => {
    const collect = collects.data.find(c => c.id === collectId);
    if (!collect) return 'Coleta';
    return [formatDate(collect.planned_date), collect.material?.name].filter(Boolean).join(' · ');
  };

  const describeContestedObject = (contestation: Contestation): string => {
    if (contestation.object_type === 'BILLING_CYCLE') return 'Medição inteira';
    if (contestation.object_type === 'COLLECT') return collectLabel(contestation.object_id);
    const owner = collects.data.find(c => c.transaction_id === contestation.object_id);
    return owner ? `Lançamento de ${collectLabel(owner.id)}` : 'Lançamento financeiro';
  };

  const boletos = (invoice.cr_data ?? []).filter(
    b => b.cod_titulo && b.boleto_gerado === 'S' && b.status !== 'RECEBIDO' && b.status !== 'CANCELADO'
  );
  const nfs = invoice.nf_data ?? [];
  const faturaPrefix = invoice.numero_pedido ? `Fatura ${invoice.numero_pedido}` : null;

  // RN-13: as duas apresentações (botão direto e lista de parcelas/notas)
  // registram o mesmo nome de ação — do contrário a métrica se divide sem
  // motivo de negócio.
  const handleBoleto = async (parcela: CrParcela) => {
    trackButtonClick({ buttonLabel: 'financeiro_visualizar_boleto', fatura_id: id });
    const title = [faturaPrefix, boletos.length > 1 ? `Parcela ${parcela.parcela}` : null].filter(Boolean).join(' · ');
    const subtitle = [
      parcela.vencimento ? `Vence ${formatDate(parcela.vencimento)}` : null,
      formatCurrency(parcela.valor),
    ].filter(Boolean).join(' · ');
    setModal({ open: true, tipo: 'boleto', url: null, isLoading: true, fatura: { title, subtitle } });
    const url = await fetchInvoiceBankSlipUrl(parcela.cod_titulo);
    setModal(prev => ({ ...prev, url, isLoading: false }));
  };

  const handleNf = (nf: NfItem) => {
    trackButtonClick({ buttonLabel: 'financeiro_visualizar_nota_fiscal', fatura_id: id });
    // TODO: Filtrar apenas NFs de SP quando o backend retornar campo identificador de município.
    // Hoje todas as NFs emitidas são de SP, então exibimos o botão sempre.
    // Referência: https://nfe.prefeitura.sp.gov.br — URL válida apenas para SP.
    const url = `https://nfe.prefeitura.sp.gov.br/contribuinte/notaprint.aspx?ccm=${nf.emissor}&nf=${nf.numero}&cod=${nf.verificador}`;
    window.open(url, '_blank');
  };

  const documentButtons = (nfs.length > 0 || boletos.length > 0) && (
    <div className="flex flex-wrap items-center gap-2">
      {nfs.length === 1 && (
        <Button
          variant="outline"
          size="sm"
          data-testid="financeiro-ver-nota-fiscal"
          onClick={() => handleNf(nfs[0])}
        >
          <FileText className="h-4 w-4 mr-2" />
          Nota Fiscal
        </Button>
      )}
      {nfs.length > 1 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="financeiro-ver-nota-fiscal">
              <FileText className="h-4 w-4 mr-2" />
              Nota Fiscal
              <ChevronDown className="h-4 w-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Notas fiscais</DropdownMenuLabel>
            {nfs.map(nf => (
              <DropdownMenuItem key={nf.cod_nf} onClick={() => handleNf(nf)}>
                NF {nf.numero}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {boletos.length === 1 && (
        <Button
          variant="outline"
          size="sm"
          data-testid="financeiro-ver-boleto"
          onClick={() => handleBoleto(boletos[0])}
        >
          <FileDown className="h-4 w-4 mr-2" />
          Boleto
        </Button>
      )}
      {boletos.length > 1 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="financeiro-ver-boleto">
              <FileDown className="h-4 w-4 mr-2" />
              Boleto
              <ChevronDown className="h-4 w-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Parcelas</DropdownMenuLabel>
            {boletos.map(b => (
              <DropdownMenuItem key={b.cod_titulo} onClick={() => handleBoleto(b)}>
                Parcela {b.parcela} — {formatDate(b.vencimento)} — {formatCurrency(b.valor)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );

  return (
    <div className="px-6 py-4 space-y-4">
      {/* Seção A — Título, estado e ação da medição, em uma linha (protótipo da PD) */}
      <section className="space-y-3">
        <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <div className="flex items-center gap-3">
              <Text as="h1" variant="heading" size="xlarge" className="text-neutral-900">
                Detalhamento da {isInvoiced ? 'Fatura' : 'Medição'}
              </Text>
              {/* Status de fatura e de medição são de universos diferentes — mostrar os dois ao
                  mesmo tempo sugere um vínculo entre eles que não existe. Uma vez faturada, só o
                  estado da fatura importa para o gerador; antes disso, só o da medição. */}
              {isInvoiced
                ? invoice.status && <InvoiceStatusBadge status={invoice.status} />
                : invoice.release_status && (
                    <MeasurementStatusBadge
                      releaseStatus={invoice.release_status}
                      wrap={false}
                      awaitingPo={invoice.awaiting_po}
                    />
                  )}
            </div>
            {/* Contestar à esquerda, aprovar à direita (retorno de QA, 24/08/2026): a ação que
                encerra o fluxo fica na ponta, onde a leitura termina, e a ação reversível vem
                antes dela. */}
            <div className="flex flex-wrap items-center gap-2">
              {/* MC-292 — primeiro do grupo, como no layout do card: é ação utilitária, e as que
                  movem o fluxo (contestar, aprovar) ficam à direita, onde a leitura termina.
                  Fora do Modo Demo, como no extrato (`OrdersTable`): o arquivo sai com os dados
                  reais, e mascará-lo produziria uma planilha que não serve para nada. */}
              {!isDemoMode && (
                <Button
                  variant="outline"
                  onClick={handleExportCSV}
                  disabled={isExporting || collects.pagination.count === 0}
                >
                  {isExporting ? 'Baixando...' : 'Baixar CSV'}
                </Button>
              )}
              {/* Um botão só: o que vai ser contestado (a medição inteira ou coletas avulsas) é
                  escolhido no card logo abaixo, e não em dois botões distantes um do outro. Fica
                  à esquerda do Aprovar e continua na tela com o card aberto — sumir dali tirava a
                  referência de onde a escolha nasceu.
                  Medição já contestada não é oferecida de novo: a RN-03 recusa a segunda abertura. */}
              {isContestable && !measurementContestation && (
                <Button variant="outline" onClick={() => setChoosingContestation(true)}>
                  Contestar
                </Button>
              )}
              {isApprovable && (
                <Button
                  onClick={() => setConfirmingApproval(true)}
                  disabled={approve.isPending}
                >
                  {approve.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Aprovar medição
                </Button>
              )}
              {showsSupportContact && (
                <Button
                  variant="outline"
                  onClick={() => {
                    trackButtonClick({ buttonLabel: 'financeiro_falar_com_suporte' });
                    openCloudHumansChat();
                  }}
                >
                  <Headphones className="mr-2 h-4 w-4" />
                  Falar com suporte Musa
                </Button>
              )}
            </div>
          </div>

          {/* Os números do ciclo formam um grupo só: a medição e, quando existe, a fatura que
              saiu dela. Havia uma linha separando os dois — mas eles se leem juntos, e o traço
              cortava um bloco coeso. */}
          <div className="space-y-3 px-4 pb-3">
            <div className="flex flex-wrap gap-x-8 gap-y-2">
              <DetailField label="Nº da medição" value={invoice.sfm_id} />
              <DetailField label="Competência" value={formatCompetencia(invoice.billing_period)} />
              {deadline && (
                <DetailField label="Prazo de contestação" value={formatDeadline(deadline)} />
              )}
              {invoice.po_number && <DetailField label="PO" value={invoice.po_number} />}
            </div>

            {isInvoiced && (
              <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
                <DetailField label="Nº da fatura" value={numeroFatura} />
                <DetailField label="Vencimento" value={formatDueDate(invoice.due_date)} />
                {!isDemoMode && documentButtons}
              </div>
            )}
          </div>

          {/* Quem paga é outro assunto dos números acima — o agrupamento agora vem do fundo, e
              não de um traço. `neutral-100` porque `neutral-50` no DS é branco e não pintava nada.
              A guarda ficava dentro da div: sem `paying_generator` sobrava uma faixa vazia. */}
          {invoice.paying_generator && (
            <div className="flex flex-wrap gap-x-8 gap-y-2 bg-neutral-100 px-4 py-2.5">
              <DetailField
                label="Estabelecimento"
                value={maskGeneratorName(invoice.paying_generator.name, isDemoMode)}
              />
              <DetailField
                label="CNPJ"
                value={
                  isDemoMode
                    ? maskCNPJ(invoice.paying_generator.document, isDemoMode)
                    : formatCnpj(invoice.paying_generator.document)
                }
              />
            </div>
          )}
        </div>

        {contestationsUnavailable && (
          <Alert variant="destructive">
            <AlertDescription>
              Não foi possível carregar as contestações desta medição. O histórico pode estar
              incompleto e a abertura fica indisponível até a consulta voltar — recarregue a página
              em alguns instantes.
            </AlertDescription>
          </Alert>
        )}

        {/* Escolha do escopo, logo abaixo do detalhamento: as duas opções aparecem juntas, e não
            em dois botões separados por meia tela. Interruptores excludentes — ligar um desliga
            o outro; nenhum ligado significa que ainda não escolheu. */}
        {choosingContestation && (
          <div className="space-y-3 rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Text variant="label" size="small" className="text-neutral-900">
                O que você quer contestar?
              </Text>
              <Button onClick={cancelContestation}>Cancelar</Button>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="contestar-medicao" className="cursor-pointer">
                  <Text variant="body" size="small" className="text-neutral-800">
                    Contestar medição inteira
                  </Text>
                </Label>
                <Switch
                  id="contestar-medicao"
                  checked={contestingMeasurement}
                  onCheckedChange={(on) => pickContestationMode('measurement', on)}
                />
              </div>

              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="contestar-coletas" className="cursor-pointer">
                  <Text variant="body" size="small" className="text-neutral-800">
                    Contestar coletas individualmente
                  </Text>
                </Label>
                <Switch
                  id="contestar-coletas"
                  checked={selectingCollects}
                  onCheckedChange={(on) => pickContestationMode('collects', on)}
                />
              </div>
            </div>

            {selectingCollects && (
              <Text variant="body" size="small" className="text-neutral-500">
                Marque as coletas na tabela abaixo.
              </Text>
            )}
          </div>
        )}

        {contestingMeasurement && (
          <ContestationForm
            objectIds={{ BILLING_CYCLE: invoice.sfm_id }}
            motiveGroups={MEASUREMENT_MOTIVE_GROUPS}
            onCancel={exitSelection}
            onOpened={cancelContestation}
          />
        )}
      </section>

      {/* Duas seções independentes. A da medição só existe quando alguém aprovou; a de
          contestações segue as próprias regras. Uma medição pode ter as duas, uma só, ou nenhuma. */}
      <MeasurementHistory approval={approval} />

      <ContestationHistory
        contestations={cycleContestations}
        describeObject={describeContestedObject}
        isLoading={isLoadingContestations && cycleContestations.length === 0}
      />

      {nfs.length > 0 && <TaxChangeNoticeAccordion />}

      {/* Seção D — Resumo financeiro */}
      <section>
        <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
          <div className="px-4 pt-4 pb-2">
            <Text as="h3" variant="heading" size="large" className="text-neutral-900">
              Resumo financeiro
            </Text>
          </div>

          {!breakdown && (
            <div className="px-4 pb-3">
              <Text variant="body" size="small" className="text-neutral-500">
                {isFixedValue
                  ? 'Valor fixo contratado, sem composição por coleta.'
                  : 'Composição de valores indisponível para esta medição.'}
              </Text>
            </div>
          )}

          {breakdown && (
            <div className="space-y-2 px-4 pb-3">
              <Text variant="caption" size="xsmall" className="uppercase tracking-wide text-neutral-500">
                Subtotal
              </Text>
              <div className="flex justify-between">
                <Text variant="body" size="small" className="text-neutral-700">Coletas</Text>
                <Text variant="body" size="small" className="tabular-nums">{formatCurrency(maskNumericValue(breakdown.credits, isDemoMode))}</Text>
              </div>
              {breakdown.rentals > 0 && (
                <div className="flex justify-between">
                  <Text variant="body" size="small" className="text-neutral-700">Locações</Text>
                  <Text variant="body" size="small" className="tabular-nums">{formatCurrency(maskNumericValue(breakdown.rentals, isDemoMode))}</Text>
                </div>
              )}
              {breakdown.debits < 0 && (
                <>
                  <Text variant="caption" size="xsmall" className="pt-2 uppercase tracking-wide text-neutral-500">
                    Descontos
                  </Text>
                  <div className="flex justify-between">
                    <Text variant="body" size="small" className="text-neutral-700">Descontos aplicados</Text>
                    <Text variant="body" size="small" className="tabular-nums text-success-600">- {formatCurrency(maskNumericValue(Math.abs(breakdown.debits), isDemoMode))}</Text>
                  </div>
                </>
              )}
            </div>
          )}

          {/* A única linha que sobra na tela — e é justamente por ser a única que ela significa
              alguma coisa: separa a composição do total. `neutral-100` porque `neutral-50` é
              branco no DS e o fill nunca apareceu. */}
          <div className="flex justify-between border-t border-neutral-200 bg-neutral-100 px-4 py-3">
            <Text variant="label" className="text-neutral-900">Total da medição</Text>
            <Text variant="label" className="tabular-nums text-neutral-900">{formatCurrency(maskNumericValue(invoice.value, isDemoMode))}</Text>
          </div>
        </div>
      </section>

      {/* Seção E — Coletas faturadas */}
      <section className="pb-4">
        {(() => {
          const pagination = collects.pagination;
          const totalPages = pagination.last_page;
          const showPagination = totalPages > 1;

          const showValue = !isFixedValue && collects.data.some(c => c.value != null);

          /** Derivar o `colSpan` das linhas aninhadas daqui evita a contagem manual. */
          const collectColumns = [
            ...(selectingCollects ? ['Contestar'] : []),
            'Data',
            'Estabelecimento',
            'Resíduo',
            'Acondicionamento',
            'Unidades',
            'Peso (kg)',
            ...(showValue ? ['Valor (R$)'] : []),
            'MTR',
            'Status coleta',
            'Cálculo aplicado',
          ];

          return (
            <>
              {/* O modo de seleção agora é ligado no card lá em cima; aqui sobra só a saída, ao
                  alcance de quem está marcando as linhas e não quer subir a tela para desistir. */}
              {selectingCollects && (
                <div className="mb-3 flex items-center justify-end">
                  <Button variant="outline" size="sm" onClick={exitSelection}>
                    Cancelar seleção
                  </Button>
                </div>
              )}
            <Card className="overflow-hidden rounded-2xl border-neutral-200 bg-neutral-50 shadow-sm">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex flex-col gap-0.5">
                    {/* `CardTitle` já é um `<h3>` — `Text` aqui só cuida da tipografia
                        (heading/large), sem duplicar a tag semântica. */}
                    <CardTitle>
                      <Text as="span" variant="heading" size="large" className="text-neutral-900">
                        Coletas Faturadas
                      </Text>
                    </CardTitle>
                    {pagination.count > 0 && (
                      <Text variant="body" size="small" className="text-neutral-300 text-xs">
                        {selectedCollects.size > 0
                          ? `${selectedCollects.size} ${selectedCollects.size === 1 ? 'coleta selecionada' : 'coletas selecionadas'}`
                          : `${collects.data.length} de ${pagination.count} registros`}
                      </Text>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-0">
                {/* Tabela, skeleton de refetch ou empty state */}
                {isFetching ? (
                  <TableSkeleton rows={Math.min(pageSize, collects.data.length || pageSize)} />
                ) : collects.data.length === 0 ? (
                  <div className="text-center py-12 px-6">
                    <FileText className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                    <Text>Esta fatura não possui coletas vinculadas.</Text>
                  </div>
                ) : (
                  <div className="px-4 md:px-6">
                    <div className="bg-white rounded-lg border border-neutral-200 overflow-x-auto">
                      <DataTable>
                        <DataTableHeader>
                          <DataTableRow>
                            {collectColumns.map(column => (
                              <DataTableHead key={column}>{column}</DataTableHead>
                            ))}
                          </DataTableRow>
                        </DataTableHeader>
                        <DataTableBody>
                          {collects.data.map(collect => {
                            const activeContestation = activeForCollect(collect);
                            const isContestingThis = selectedCollects.has(collect.id);
                            const isCalcOpen = expandedCalc.has(collect.id);

                            return (
                            <Fragment key={collect.id}>
                            <DataTableRow>
                              {selectingCollects && (
                                <DataTableCell>
                                  {activeContestation ? (
                                    <Text variant="body" size="small" className="text-neutral-400">—</Text>
                                  ) : (
                                    <Checkbox
                                      checked={isContestingThis}
                                      onCheckedChange={() => toggleCollectSelection(collect.id)}
                                      aria-label={`Contestar coleta de ${formatDate(collect.planned_date)}`}
                                    />
                                  )}
                                </DataTableCell>
                              )}
                              <DataTableCell>{formatDate(collect.planned_date)}</DataTableCell>
                              <DataTableCell>{collect.establishment ? maskEstablishmentName(collect.establishment.name, isDemoMode) : '—'}</DataTableCell>
                              <DataTableCell>{collect.material?.name ?? '—'}</DataTableCell>
                              <DataTableCell>{collect.packaging?.name ?? '—'}</DataTableCell>
                              <DataTableCell>{collect.units != null ? collect.units : '—'}</DataTableCell>
                              <DataTableCell>{collect.weight != null ? collect.weight : '—'}</DataTableCell>
                              {showValue && (
                                <DataTableCell>
                                  <Text variant="body" size="small" className="text-neutral-900">
                                    {collect.value != null ? formatCurrency(maskNumericValue(collect.value, isDemoMode)) : '—'}
                                  </Text>
                                </DataTableCell>
                              )}
                              <DataTableCell>
                                <MtrCell mtrNumbers={collect.mtr_numbers} />
                              </DataTableCell>
                              <DataTableCell>
                                {/* A coluna é "Status coleta": com contestação ativa a coleta
                                    está **Contestada**, o mesmo rótulo da medição em
                                    `release_status = CONTESTED`. O estado da contestação
                                    ("Aberta", "Em análise"…) responde outra pergunta e vive no
                                    Histórico de contestações. */}
                                <CollectStatusBadge
                                  status={activeContestation ? COLLECT_STATUS_CONTESTED : collect.status}
                                />
                              </DataTableCell>
                              <DataTableCell>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => toggleCalc(collect.id)}
                                  aria-expanded={isCalcOpen}
                                  aria-label={isCalcOpen ? 'Ocultar cálculo aplicado' : 'Ver cálculo aplicado'}
                                >
                                  <ChevronDown
                                    className={`h-4 w-4 transition-transform ${isCalcOpen ? 'rotate-180' : ''}`}
                                  />
                                </Button>
                              </DataTableCell>
                            </DataTableRow>

                            {isCalcOpen && (
                              <DataTableRow>
                                <DataTableCell colSpan={collectColumns.length} className="bg-neutral-50">
                                  <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-4">
                                    <Text
                                      variant="caption"
                                      size="xsmall"
                                      className="shrink-0 font-semibold text-neutral-500"
                                    >
                                      Cálculo aplicado
                                    </Text>
                                    <div className="min-w-0 flex-1">
                                      <CollectCalculationDetails
                                        cycleId={invoice.sfm_id}
                                        collectId={collect.id}
                                        variant="compact"
                                      />
                                    </div>
                                  </div>
                                </DataTableCell>
                              </DataTableRow>
                            )}

                            {isContestingThis && (
                              <DataTableRow>
                                <DataTableCell colSpan={collectColumns.length} className="bg-neutral-50">
                                  <ContestationForm
                                    objectIds={{ COLLECT: collect.id, TRANSACTION: collect.transaction_id }}
                                    // RN-04: com a fatura contestada, as transações do ciclo não podem
                                    // ser contestadas à parte. Os motivos de coleta seguem (RN-40/S19).
                                    motiveGroups={collectMotiveGroups(
                                      !!collect.transaction_id && !measurementContestation,
                                    )}
                                    onCancel={() => toggleCollectSelection(collect.id)}
                                    onOpened={() => toggleCollectSelection(collect.id)}
                                  />
                                </DataTableCell>
                              </DataTableRow>
                            )}
                            </Fragment>
                            );
                          })}
                        </DataTableBody>
                      </DataTable>
                    </div>
                  </div>
                )}

                {/* Controles de paginação */}
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 px-6">
                  {pagination.count > 10 ? (
                    <div className="flex items-center gap-2">
                      <Text variant="label" size="small" className="text-neutral-500 whitespace-nowrap">
                        Linhas por página
                      </Text>
                      <Select
                        value={String(pageSize)}
                        onValueChange={(v) => {
                          setPageSize(Number(v) as 10 | 25 | 50);
                          setPage(1);
                        }}
                      >
                        {/* Largura suficiente para o valor não ficar cortado pelo chevron. */}
                        <SelectTrigger className="h-8 w-[88px]" aria-label="Linhas por página">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div />
                  )}
                  {showPagination && (
                    <DataPagination
                      page={page}
                      pageSize={pageSize}
                      total={pagination.count}
                      onChange={setPage}
                    />
                  )}
                </div>
              </CardContent>
            </Card>
            </>
          );
        })()}
      </section>

      <InvoiceDocumentModal
        open={modal.open}
        onOpenChange={open => setModal(prev => ({ ...prev, open }))}
        fatura={modal.fatura}
        tipo={modal.tipo}
        documentUrl={modal.url}
        isLoadingUrl={modal.isLoading}
      />

      <ApproveMeasurementPoDialog
        open={confirmingApproval && requiresPo}
        onOpenChange={setConfirmingApproval}
        onSubmit={handleApproveWithPo}
      />

      <AlertDialog open={confirmingApproval && !requiresPo} onOpenChange={setConfirmingApproval}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Aprovar esta medição?</AlertDialogTitle>
            <AlertDialogDescription>
              A medição segue para faturamento e o prazo para contestar encerra agora. Depois da
              aprovação não é possível contestar por esta tela.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={approve.isPending}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={event => {
                event.preventDefault();
                void handleApprove();
              }}
              disabled={approve.isPending}
            >
              {approve.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Aprovando...
                </>
              ) : (
                'Aprovar medição'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
